import React, { useEffect, useState } from "react";
import { byggKategoriTre } from "../utils/kategoriTre";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const BUTIKKER = [
  { value: "royken", label: "Sport 1 Røyken" },
  { value: "slemmestad", label: "Sport 1 Slemmestad" },
];

// Speiler KOLONNE_ALIASER i src/routes/varelager.js - oppdater begge steder sammen.
// "krav" styrer kun fargen på merkelappen i oversikten, ikke noe backend håndhever.
// Kategori/Hovedkategori leses ikke fra filen (kategoritreet bygges for hånd).
const KOLONNE_OVERSIKT = [
  {
    felt: "EAN",
    krav: "påkrevd",
    aliaser: ["EAN", "EAN-nummer", "EANnummer", "Strekkode", "Barcode", "GTIN", "GINT", "GITN"],
    merknad: "Identifiserer den enkelte varianten (størrelse og farge). Rader uten EAN hoppes over. EAN endres aldri på en vare som finnes fra før.",
  },
  {
    felt: "Navn",
    krav: "anbefalt",
    aliaser: ["Navn", "Produkt", "Produktnavn", "Varenavn", "Varetekst"],
    merknad: "Produktnavnet. Merke + navn + farge samler størrelsene til samme produkt. Mangler navnet, brukes varenummeret (eller EAN) i stedet, og raden blir et eget produkt.",
  },
  {
    felt: "Varenummer",
    krav: "valgfritt",
    aliaser: ["Varenummer", "Produktkode", "Vare nr", "Varenr"],
    merknad: "Lagres på produktet. Brukes som navn når Navn mangler.",
  },
  {
    felt: "Merke",
    krav: "valgfritt",
    aliaser: ["Merke", "Varemerke", "Merkenavn", "Produsent", "Brand"],
  },
  {
    felt: "Farge",
    krav: "valgfritt",
    aliaser: ["Farge", "Fargenavn", "Color", "Colour"],
    merknad: "Hver farge blir et eget produkt med egen side, ikke et fargevalg under ett produkt.",
  },
  {
    felt: "Størrelse",
    krav: "valgfritt",
    aliaser: ["Størrelse", "Storrelse", "Size", "Str"],
    merknad: "Fritekst, tas med som den er (f.eks. rammestørrelser i tommer). Nye varer uten størrelse får «O/S».",
  },
  {
    felt: "Modell",
    krav: "valgfritt",
    aliaser: ["Modell", "Model", "Modellnummer", "Modellnr", "Modell nr", "Modellkode", "Model no", "Model number", "Modelnumber"],
    merknad: "Modellkoden fra leverandøren (bokstaver og tall). Viser hvilke varer som hører sammen på tvers av størrelse, lengde, stivhet osv., så like varer kan slås sammen senere. Lagres på hver variant.",
  },
  {
    felt: "Artikkelnummer",
    krav: "valgfritt",
    aliaser: ["Artikkelnummer", "Art.nr", "Artnr", "MyStore art.nr", "MyStore artikkelnummer"],
    merknad: "Lagres på hver variant.",
  },
  {
    felt: "Pris (veilpris)",
    krav: "valgfritt",
    aliaser: ["Pris", "Utpris", "Utsalgspris", "Veilpris", "Price"],
    merknad: "Felles for begge butikker.",
  },
  {
    felt: "Tilbudspris",
    krav: "valgfritt",
    aliaser: ["Tilbudspris", "Tilbud", "Salgspris", "Kampanjepris", "Kampanjepriser"],
    merknad: "Felles for begge butikker.",
  },
  {
    felt: "Kostpris",
    krav: "valgfritt",
    aliaser: ["Kostpris", "Innkjøpspris", "Innkjopspris", "Kost", "Cost", "Costprice", "Kostpris pr. enhet"],
  },
  {
    felt: "Lager",
    krav: "valgfritt",
    aliaser: ["Lager", "Antall", "Beholdning", "Stock", "Saldo", "Lager Antall"],
    merknad: "Skrives bare til lageret for butikken valgt i steg 1. En fil med bare EAN og Lager/Pris er en ren oppdatering, se under tabellen.",
  },
];

const KRAV_STIL = {
  påkrevd: "bg-red-50 text-red-700 border-red-200",
  anbefalt: "bg-blue-50 text-blue-700 border-blue-200",
  valgfritt: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function Varelager() {
  const [butikk, setButikk] = useState("");
  const [kategoriId, setKategoriId] = useState("");
  const [kategoriTre, setKategoriTre] = useState([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // Overstyringsdropdownen under henter live fra Kategorier-siden (ikke lenger
  // en hardkodet sykkel-liste) - viser dermed enhver hovedgruppe/mellomgruppe/
  // undergruppe som er opprettet der, uansett varetype. Hentes på nytt hver gang
  // dropdownen åpnes (ikke bare ved sideinnlasting) - siden er gjerne stående åpen
  // i en fane mens man bygger ut kategoritreet i en annen, og en nylig opprettet
  // undergruppe skal være valgbar uten å måtte laste siden på nytt.
  function hentKategoriTre() {
    fetch(`${API_BASE_URL}/kategorier`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Kunne ikke hente kategorier"))))
      .then((data) => setKategoriTre(byggKategoriTre(data.filter((k) => k.aktiv))))
      .catch(() => setKategoriTre([]));
  }

  useEffect(() => {
    hentKategoriTre();
  }, []);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!butikk) {
      setError("Velg butikk før du laster opp filen");
      e.target.value = "";
      return;
    }
    if (kategoriId) {
      const valgtKategori = kategoriTre.find((k) => k._id === kategoriId);
      const advarsel =
        `Alle rader i filen settes til kategorien «${valgtKategori?.navn || "valgt kategori"}» — ` +
        `også produkter som allerede finnes fra før, ikke bare nye. Fortsette?`;
      if (!window.confirm(advarsel)) {
        e.target.value = "";
        return;
      }
    }
    setImporting(true);
    setError("");
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("butikk", butikk);
      if (kategoriId) {
        formData.append("kategoriId", kategoriId);
      }
      const res = await fetch(`${API_BASE_URL}/varelager/import`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Kunne ikke importere filen");
      }

      // Store filer importeres i bakgrunnen på serveren (unngår Herokus faste
      // 30-sekunders request-timeout) - får vi en jobbId tilbake, poller vi
      // fremdriften til jobben er ferdig i stedet for å vente på ett svar.
      if (data.jobbId) {
        await pollImportjobb(data.jobbId);
      } else {
        setResult(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  }

  async function pollImportjobb(jobbId) {
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const res = await fetch(`${API_BASE_URL}/varelager/import/${jobbId}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Mistet kontakt med importjobben");
      }
      if (data.status === "feilet") {
        throw new Error(data.feilmelding || "Importen feilet");
      }
      setResult(data);
      if (data.status === "ferdig") {
        return;
      }
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-xl font-semibold text-gray-900 mb-1">Varelager</h1>
      <p className="text-sm text-gray-500 mb-6">
        Importerer varekatalogen fra backoffice inn i nettbutikkens produktdatabase.
        Bilder, SEO-felt og produktbeskrivelse legges alltid inn manuelt etterpå —
        de importeres aldri herfra.
      </p>

      <div className="border border-gray-200 rounded-xl p-4 mb-8">
        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">
            1. Velg hvilket butikklager filen gjelder
          </p>
          <div className="flex gap-4">
            {BUTIKKER.map((b) => (
              <label key={b.value} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="butikk"
                  value={b.value}
                  checked={butikk === b.value}
                  onChange={(e) => setButikk(e.target.value)}
                  disabled={importing}
                />
                {b.label}
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Lagerantallet fra filen skrives kun til denne butikkens eget lagerfelt — den andre
            butikkens lager røres ikke. Pris, tilbudspris, størrelse osv. er felles for begge butikker.
          </p>
        </div>

        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">
            2. Overstyr kategori <span className="font-normal text-gray-400">(valgfritt)</span>
          </p>
          <select
            value={kategoriId}
            onChange={(e) => setKategoriId(e.target.value)}
            onFocus={hentKategoriTre}
            disabled={importing}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700"
          >
            <option value="">Ikke endre kategori</option>
            {kategoriTre.map((k) => (
              <option key={k._id} value={k._id}>
                {"   ".repeat(k.dybde)}
                {k.navn}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">
            Velges en kategori her, plasseres <strong>alle</strong> rader i filen der —
            også produkter som finnes fra før (bekreftes før opplasting), ikke bare nye. Uten
            valg beholder eksisterende produkter kategorien sin, og nye produkter får ingen
            kategori. Kategori-kolonner i filen brukes ikke, og importen oppretter aldri
            kategorier. Hoved-, mellom- og undergrupper kan velges. Listen hentes fra{" "}
            <a href="/kategorier" className="underline hover:text-gray-600">Kategorier-siden</a>,
            der kategoriene opprettes.
          </p>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <label
            className={`px-3 py-1.5 rounded-lg border text-sm font-semibold whitespace-nowrap ${
              butikk && !importing
                ? "border-gray-300 hover:bg-gray-50 text-gray-700 cursor-pointer"
                : "border-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            3. {importing ? "Importerer …" : "Last opp lagerfil"}
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleUpload}
              disabled={importing || !butikk}
            />
          </label>
          <p className="text-sm text-gray-500">
            {error ? (
              <span className="text-red-600">{error}</span>
            ) : result?.status === "kjører" ? (
              <>
                Importerer … {result.behandlet} / {result.totalRader} rader behandlet.
              </>
            ) : result ? (
              <>
                {result.kunLagerimport
                  ? "Gjenkjent som ren oppdatering (kun EAN + Lager/Pris, ingen nye produkter) — "
                  : ""}
                {result.totalRader} rader lest, {result.opprettetEllerOppdatertVarianter} varianter
                opprettet/oppdatert
                {result.hoppetOverUtenEan
                  ? `, ${result.hoppetOverUtenEan} hoppet over (mangler EAN)`
                  : ""}
                {result.hoppetOverUkjentEan
                  ? `, ${result.hoppetOverUkjentEan} hoppet over (ukjent EAN, ikke opprettet som nytt produkt)`
                  : ""}
                {result.feil?.length ? `, ${result.feil.length} feilet` : ""}.
              </>
            ) : (
              "Ingen fil importert ennå."
            )}
          </p>
        </div>

        <div className="text-xs text-gray-500 border-t border-gray-100 pt-3">
          <p className="mb-2">
            Excel-fil fra backoffice, én rad per EAN (størrelse/farge). Kolonneoverskrifter kan
            hete hva som helst av det som er listet under «Godtatte kolonnenavn i filen»
            (store/små bokstaver spiller ikke rolle). Andre kolonner i filen ignoreres, også
            Kategori og Hovedkategori — kategorien velges i steg 2.
          </p>
          <div className="overflow-x-auto mb-2 border border-gray-100 rounded-lg">
            <table className="text-xs w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-left font-semibold px-2.5 py-1.5 whitespace-nowrap">Felt i produktkatalogen</th>
                  <th className="text-left font-semibold px-2.5 py-1.5 whitespace-nowrap">Krav</th>
                  <th className="text-left font-semibold px-2.5 py-1.5">Godtatte kolonnenavn i filen</th>
                  <th className="text-left font-semibold px-2.5 py-1.5">Merknad</th>
                </tr>
              </thead>
              <tbody>
                {KOLONNE_OVERSIKT.map((rad, idx) => (
                  <tr key={rad.felt} className={idx % 2 === 1 ? "bg-gray-50/60" : undefined}>
                    <td className="px-2.5 py-1.5 font-semibold text-gray-700 align-top whitespace-nowrap">
                      {rad.felt}
                    </td>
                    <td className="px-2.5 py-1.5 align-top">
                      <span
                        className={`inline-block px-1.5 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${KRAV_STIL[rad.krav]}`}
                      >
                        {rad.krav}
                      </span>
                    </td>
                    <td className="px-2.5 py-1.5 align-top">
                      <div className="flex flex-wrap gap-1">
                        {rad.aliaser.map((alias) => (
                          <code
                            key={alias}
                            className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 text-[11px] text-gray-700 whitespace-nowrap"
                          >
                            {alias}
                          </code>
                        ))}
                      </div>
                    </td>
                    <td className="px-2.5 py-1.5 align-top text-gray-500">{rad.merknad || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mb-1.5">
            Merke + navn + farge grupperer rader til samme produkt — <strong>hver farge blir et
            eget, uavhengig produkt</strong> med egen side, ikke et fargevalg under ett produkt.
            EAN identifiserer den enkelte størrelsen innenfor fargen. Nye produkter opprettes
            deaktivert (aktiv=false) — aktiver dem når bilde, beskrivelse og SEO er lagt inn
            manuelt, per farge/produkt. Totalt lager (begge butikker samlet) vises i
            nettbutikk-appen som summen av Lager Røyken + Lager Slemmestad.
          </p>
          <p>
            Inneholder filen <strong>kun EAN og Lager/Antall og/eller Pris</strong> (ingen av
            de andre kolonnene over), tolkes den som en ren oppdatering — ment til å kjøres
            jevnlig for å friske opp beholdning og pris på produkter som allerede er importert.
            Da oppdateres kun lager og pris på EAN som finnes fra før; ukjente EAN hoppes over,
            og ingen nye produkter opprettes.
          </p>
        </div>

        {result?.feil?.length > 0 && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
            <p className="font-semibold mb-1">{result.feil.length} rad(er) feilet:</p>
            <ul className="list-disc list-inside">
              {result.feil.slice(0, 20).map((f, idx) => (
                <li key={idx}>
                  Rad {f.rad} (EAN {f.ean}): {f.melding}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
